/**
 * Analytics Service
 * 
 * Service for Meta Marketing API analytics
 */

import axios from 'axios'
import { API_CONFIG } from '../../shared/config'
import { getAuthToken } from '@trafiflow/shared-ui'

// Types
export interface ConnectionStatus {
  connected: boolean
  status: 'active' | 'disconnected' | 'error' | 'pending'
  ad_account_id?: string
  account_name?: string
  last_sync_at?: string | null
  error_message?: string
}

export interface AnalyticsSummary {
  date_range: { start: string; end: string }
  totals: {
    spend: number
    revenue: number
    impressions: number
    clicks: number
    purchases: number
    roas: number
    ctr: number
    cpc: number
    cpa: number
  }
  last_sync_at?: string | null
}

export interface CampaignMetrics {
  campaign_id: string
  campaign_name: string
  spend: number
  revenue: number
  impressions: number
  clicks: number
  purchases: number
  roas: number
  ctr: number
  cpc: number
  cpa: number
}

export interface CampaignsResponse {
  date_range: { start: string; end: string }
  campaigns: CampaignMetrics[]
}

export interface ConnectMetaRequest {
  access_token: string
  ad_account_id: string
}

export interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: string
}

// Create axios instance for analytics API
const analyticsClient = axios.create({
  baseURL: API_CONFIG.analyticsUrl,
  timeout: API_CONFIG.timeout,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add auth interceptor
// API Gateway Cognito User Pools Authorizer expects: Authorization: Bearer <token>
analyticsClient.interceptors.request.use(
  async (config) => {
    const token = await getAuthToken()
    if (token && config.headers) {
      // API Gateway Cognito authorizer expects "Bearer " prefix
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Service methods
export const analyticsService = {
  /**
   * Get connection status
   */
  async getConnectionStatus(): Promise<ConnectionStatus> {
    try {
      const response = await analyticsClient.get<ApiResponse<ConnectionStatus>>(
        '/analytics/connections'
      )
      if (response.data.success && response.data.data) {
        return response.data.data
      }
      return { connected: false, status: 'disconnected' }
    } catch (error: unknown) {
      console.error('[AnalyticsService] Error getting connection status:', error)
      const axiosError = error as { response?: { status?: number } }
      
      // Handle 401 Unauthorized - user not logged in with Cognito
      if (axiosError.response?.status === 401) {
        return { 
          connected: false, 
          status: 'disconnected', 
          error_message: 'Authentication required. Please sign in with a valid account.' 
        }
      }
      
      return { connected: false, status: 'error', error_message: 'Failed to check connection' }
    }
  },

  /**
   * Connect Meta account
   */
  async connectMeta(request: ConnectMetaRequest): Promise<ApiResponse<ConnectionStatus>> {
    try {
      const response = await analyticsClient.post<ApiResponse<ConnectionStatus>>(
        '/analytics/connections',
        request
      )
      return response.data
    } catch (error: unknown) {
      console.error('[AnalyticsService] Error connecting:', error)
      const axiosError = error as { response?: { data?: ApiResponse<ConnectionStatus> } }
      return axiosError.response?.data || { success: false, error: 'Failed to connect' }
    }
  },

  /**
   * Disconnect Meta account
   */
  async disconnectMeta(): Promise<ApiResponse<void>> {
    try {
      const response = await analyticsClient.delete<ApiResponse<void>>(
        '/analytics/connections'
      )
      return response.data
    } catch (error) {
      console.error('[AnalyticsService] Error disconnecting:', error)
      return { success: false, error: 'Failed to disconnect' }
    }
  },

  /**
   * Trigger manual sync
   */
  async triggerSync(): Promise<ApiResponse<void>> {
    try {
      const response = await analyticsClient.post<ApiResponse<void>>(
        '/analytics/connections/sync'
      )
      return response.data
    } catch (error) {
      console.error('[AnalyticsService] Error triggering sync:', error)
      return { success: false, error: 'Failed to trigger sync' }
    }
  },

  /**
   * Get analytics summary
   */
  async getSummary(startDate: string, endDate: string): Promise<ApiResponse<AnalyticsSummary>> {
    try {
      const response = await analyticsClient.get<ApiResponse<AnalyticsSummary>>(
        '/analytics/summary',
        { params: { startDate, endDate } }
      )
      return response.data
    } catch (error) {
      console.error('[AnalyticsService] Error getting summary:', error)
      return { success: false, error: 'Failed to load summary' }
    }
  },

  /**
   * Get campaign metrics
   */
  async getCampaigns(startDate: string, endDate: string): Promise<ApiResponse<CampaignsResponse>> {
    try {
      const response = await analyticsClient.get<ApiResponse<CampaignsResponse>>(
        '/analytics/campaigns',
        { params: { startDate, endDate } }
      )
      return response.data
    } catch (error) {
      console.error('[AnalyticsService] Error getting campaigns:', error)
      return { success: false, error: 'Failed to load campaigns' }
    }
  },
}

export default analyticsService

